import { motion } from "framer-motion";
import { useEffect, useContext } from "react";
import { LanguageContext } from "../../../context/LanguageContext";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.6, ease: "easeOut" },
  }),
};

export default function HowItWorks() {
  const { t, isRTL } = useContext(LanguageContext);
  const steps = t("howItWorksPage.steps", { returnObjects: true });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <section
      dir={isRTL ? "rtl" : "ltr"}
      className="relative bg-white py-24 overflow-hidden"
    >
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-rose-50/50 via-white to-white" />

      <div className="mx-auto max-w-7xl px-6">

        {/* Header */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="mb-24 text-center"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
            {t("howItWorksPage.hero.title")}
          </h1>
          <p className="mt-6 max-w-3xl mx-auto text-lg text-gray-600">
            {t("howItWorksPage.hero.subtitle")}
          </p>
        </motion.div>

        {/* Steps */}
        <div className="relative mx-auto max-w-4xl">
          <div className="absolute left-4 top-0 h-full w-px bg-gray-200 md:left-1/2 md:-translate-x-1/2" />

          {steps.map((step, i) => (
            <motion.div
              key={i}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              className={`relative mb-20 flex flex-col md:flex-row ${
                i % 2 === 0 ? "md:flex-row-reverse" : ""
              }`}
            >
              <div className="md:w-1/2 px-6">
                <div className="rounded-2xl border bg-white p-8 shadow-sm">
                  <span className="text-sm font-semibold text-rose-600">
                    STEP 0{step.id}
                  </span>
                  <h3 className="mt-2 text-xl font-semibold text-gray-900">
                    {step.title}
                  </h3>
                  <p className="mt-4 text-gray-600 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>

              <div className="absolute left-4 top-8 h-3 w-3 rounded-full bg-rose-600 md:left-1/2 md:-translate-x-1/2" />
            </motion.div>
          ))}
        </div>

        {/* Contact */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="mt-32 rounded-3xl bg-gray-900 p-12 text-white"
        >
          <div className="grid gap-10 md:grid-cols-2">
            <div>
              <h2 className="text-3xl font-semibold mb-6">
                {t("howItWorksPage.contact.title")}
              </h2>

              <ul className="space-y-3 text-gray-300">
                <li>
                  <strong className="text-white">
                    {t("howItWorksPage.contact.general")}:
                  </strong>{" "}
                  contact@teqa.app
                </li>
                <li>
                  <strong className="text-white">
                    {t("howItWorksPage.contact.sales")}:
                  </strong>{" "}
                  sales@teqa.app
                </li>
                <li>
                  <strong className="text-white">
                    {t("howItWorksPage.contact.support")}:
                  </strong>{" "}
                  support@teqaconnect.com
                </li>
                <li className="mt-4 whitespace-pre-line">
                  <strong className="text-white">
                    {t("howItWorksPage.contact.addressLabel")}:
                  </strong>
                  {"\n"}
                  {t("howItWorksPage.contact.address")}
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-4">
                {t("howItWorksPage.contact.follow")}
              </h3>

              <ul className="space-y-3 text-gray-300">
                <li><a href="https://www.instagram.com/teqaconnect">Instagram</a></li>
                <li><a href="https://www.facebook.com/share/17QUTjY5nJ/?mibextid=wwXIfr">Facebook</a></li>
                <li><a href="https://www.linkedin.com/company/teqaconnect/">LinkedIn</a></li>
                <li><a href="https://x.com/teqaconnect">X</a></li>
                <li className="pt-4">
                  <strong className="text-white">
                    {t("howItWorksPage.contact.blog")}:
                  </strong>{" "}
                  <a href="https://teqa.blog">teqa.blog</a>
                </li>
              </ul>
            </div>
          </div>
        </motion.div>

      </div>
    </section>
  );
}
